import { NgxPermissionsService } from 'ngx-permissions'
import Validator from '@shared/validatorjs/src/validator'
import { Map, List, fromJS } from 'immutable'
import {
  getActiveFormState,
  getPropertyState,
  getCreatePropertyState,
  isFeaturesCalculatingState,
  isPropertyReportGeneratingState,
} from './../properties.selectors'
import {
  FetchActiveFormRequest,
  UpdateField,
  UpdatePropertyRequest,
  CreatePropertyRequest,
  CalculateFeaturesRequest,
  GeneratePropertyReportRequest,
  FetchPropertyDynamicFieldsRequest,
} from './../properties.actions'
import { Router, ActivatedRoute } from '@angular/router'
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewRef,
} from '@angular/core'
import { Store, select } from '@ngrx/store'
import FormTrait from 'src/app/traits/form.trait'
import { Subscription, Observable } from 'rxjs'

@Component({
  selector: 'app-property-features',
  templateUrl: './property-features.component.html',
  styleUrls: ['./property-features.component.css'],
})
export class PropertyFeaturesComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  public propertyId: number
  public activeForm: Map<string, any>
  public evalJson: Map<string, any>
  private evalJson$: Subscription
  private activeForm$: Subscription
  public isLoading: boolean
  public isReadonly: boolean
  public status: string
  public nextStatus: string
  private parent: string
  public calculations: List<Map<string, any>>
  public isFeaturesCalculating: Observable<boolean>
  public hasReadonlyPermission: boolean = false
  public isPropertyReportGenerating: Observable<boolean>
  public buildingLayerFeatures: any
  public dynamicFieldsTranslation: any = {}

  constructor(
    public permissionsService: NgxPermissionsService,
    private store: Store<any>,
    public router: Router,
    private route: ActivatedRoute,
  ) {
    super()
  }

  ngOnInit() {
    this.propertyId = parseInt(this.route.parent.snapshot.params.id, 10)
    this.hasReadonlyPermission =
      this.permissionsService.getPermissions()['READONLY'] !== undefined
    this.parent = this.propertyId ? 'property' : 'createProperty'
    this.isFeaturesCalculating = this.store.pipe(
      select(isFeaturesCalculatingState(this.parent)),
    )
    const getState = this.propertyId ? getPropertyState : getCreatePropertyState
    this.isPropertyReportGenerating = this.store.pipe(
      select(isPropertyReportGeneratingState),
    )
    this.activeForm$ = this.store
      .pipe(select(getActiveFormState))
      .subscribe((state) => {
        this.activeForm = state.get('data')
      })

    this.evalJson$ = this.store.pipe(select(getState)).subscribe((state) => {
      this.state = state.get('data')
      this.dynamicFieldsTranslation = state
        .getIn(['dynamicFields', 'data'])
        .toJS()
      this.isLoading = state.get('isLoading')
      this.evalJson =
        state.getIn(['data', 'evaluation', 'evalJson']) || fromJS({})
      this.calculations = state.getIn(['data', 'calculations']) || fromJS([])
      const status = this.state.getIn(['status', 'name'])
      this.isReadonly =
        status === 'BILLING' ||
        this.hasReadonlyPermission ||
        status === 'ARCHIVED'

      const buildingLayerFeatures = state
        .getIn(['buildingLayerFeatures'])
        ?.toJS()

      this.buildingLayerFeatures = buildingLayerFeatures?.length
        ? buildingLayerFeatures.map((data: any) => ({ layerFeature: data }))
        : (this.buildingLayerFeatures = state
            .getIn(['data', 'building', 'buildingLayerFeatures'])
            ?.toJS())
    })

    if (this.buildingLayerFeatures?.length) {
      this.handleFeaturesCalculation()
    }

    this.store.dispatch<any>(new FetchActiveFormRequest())
    this.store.dispatch<any>(new FetchPropertyDynamicFieldsRequest())
  }

  ngOnDestroy() {
    this.activeForm$.unsubscribe()
    this.evalJson$.unsubscribe()
  }

  handleCalculate(calculations: object) {
    setTimeout(() => {
      this.store.dispatch(
        new UpdateField('calculations', calculations, `${this.parent}.data`),
      )
    }, 0)
  }

  report(propertyType: string) {
    this.store.dispatch<any>(
      new GeneratePropertyReportRequest(this.propertyId, propertyType),
    )
  }

  handleFeaturesCalculation(): void {
    this.store.dispatch(
      new CalculateFeaturesRequest({
        evalJson: this.evalJson.toJS(),
        rooftopArea: this.state.getIn(['building', 'area']),
        buildingLayerFeatures: this.buildingLayerFeatures,
        parent: this.parent,
      }),
    )
  }

  handleSelectChange(event: any): void {
    if (event.option instanceof Map) {
      this.store.dispatch(
        new UpdateField(
          event.name,
          event.option.get('label'),
          `${this.parent}.data.evaluation.evalJson`,
        ),
      )
    } else if (event.option instanceof List) {
      const option = Map(
        event.option.map((v: any) => [v.get('label'), v.get('value')]),
      )

      this.store.dispatch(
        new UpdateField(
          event.name,
          option,
          `${this.parent}.data.evaluation.evalJson`,
        ),
      )
    } else {
      if (Array.isArray(event.option)) {
        this.store.dispatch(
          new UpdateField(
            event.name,
            event.option.length ? event.option.join(',') : null,
            `${this.parent}.data.evaluation.evalJson`,
          ),
        )
      } else {
        this.store.dispatch(
          new UpdateField(
            event.name,
            event.option,
            `${this.parent}.data.evaluation.evalJson`,
          ),
        )
      }
    }

    this.handleFeaturesCalculation()
  }

  handleSelectInput({ input, name }): void {
    this.store.dispatch(
      new UpdateField(
        input.label,
        input.value,
        `${this.parent}.data.evaluation.evalJson.` + name,
      ),
    )
    this.handleFeaturesCalculation()
  }

  handleFieldChange(e: any): void {
    this.store.dispatch(
      new UpdateField(
        e.target.name,
        e.target.value,
        `${this.parent}.data.evaluation.evalJson`,
      ),
    )
    this.handleFeaturesCalculation()
  }
}
